import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    question: "How do I get started?",
    answer: "It's simple! Peruse our list of tutors and find one that you think would be a good fit for you. Once you've found a tutor you like, check their availability and schedule a session with them. You and your tutor will receive an email, and they will follow up with the details of your session. Make sure to have your math problems ready!"
  },
  {
    question: "How long will a typical session be?",
    answer: "EMOO sessions will typically be anywhere between 30-60 minutes. Tutors will aim to help the students with as many math problems and topics as needed, but each session will have a cap of 60 minutes to protect the tutor's and student's time. If your student still feels that they need help after a session, fear not! Your student can always schedule another session with a tutor, or arrange to meet with tutors weekly."
  },
  {
    question: "How much does a session cost?",
    answer: "EMOO sessions are completely free! EMOO aims to make math tutoring accessible to OUSD middle school students."
  },
  {
    question: "Where are sessions usually held?",
    answer: "Most sessions will be online, on Google Meets. However, for special circumstances, some of our tutors may offer in-person sessions either at school or in a public setting. Email us at equalmathopportunitiesoakland@gmail.com if you would like to schedule an in-person session, describing why an in-person meeting would benefit you."
  },
  {
    question: "Which math topics can tutors help me with?",
    answer: "EMOO tutors are comfortable helping students with anything ranging from basic addition to calculus. If a student needs help with a specific math concept, we recommend they inform their tutor about it in advance so that the tutor can prepare accordingly."
  }
];

const PageContainer = styled(motion.div)`
  padding: 4rem 2rem;
  background-color: #e6eef8;
  min-height: 100vh;
  font-family: 'Roboto', sans-serif;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const ContentWrapper = styled(motion.div)`
  max-width: 900px;
  width: 100%;
  background-color: #ffffff;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const Title = styled(motion.h1)`
  color: #1c2f4f;
  text-align: center;
  margin-bottom: 3rem;
  font-size: 2.8rem;
  font-weight: bold;
  position: relative;
  
  &:after {
    content: '';
    display: block;
    width: 100px;
    height: 4px;
    background-color: #1c2f4f;
    margin: 1rem auto 0;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Accordion = styled(motion.div)``;

const AccordionItem = styled(motion.div)`
  margin-bottom: 1.5rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const AccordionHeader = styled.div`
  padding: 1.5rem;
  cursor: pointer;
  background-color: #e6eef8;
  color: #1c2f4f;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.2rem;
  font-weight: 600;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #d1e0f0;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 1.2rem;
  }
`;

const AccordionContent = styled(motion.div)`
  background-color: #fff;
  color: #1c2f4f;
  font-size: 1rem;
  line-height: 1.8;
  overflow: hidden;
`;

const ExpandIcon = styled(motion.span)`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #1c2f4f;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: bold;
`;

const FaqPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = index => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <PageContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <ContentWrapper
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Title variants={itemVariants}>Frequently Asked Questions</Title>
        <Accordion>
          {faqs.map((faq, index) => (
            <AccordionItem key={index} variants={itemVariants}>
              <AccordionHeader onClick={() => toggleAccordion(index)}>
                {faq.question}
                <ExpandIcon
                  animate={{ rotate: openIndex === index ? 45 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  +
                </ExpandIcon>
              </AccordionHeader>
              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <AccordionContent
                    initial="collapsed"
                    animate="open"
                    exit="collapsed"
                    variants={{
                      open: { opacity: 1, height: "auto" },
                      collapsed: { opacity: 0, height: 0 }
                    }}
                    transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                  >
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: 0.1 }}
                    >
                      <p style={{ padding: '1.5rem' }}>{faq.answer}</p>
                    </motion.div>
                  </AccordionContent>
                )}
              </AnimatePresence>
            </AccordionItem>
          ))}
        </Accordion>
      </ContentWrapper>
    </PageContainer>
  );
};

export default FaqPage;